
import { Card } from "@/components/ui/card";
import { Plane } from "lucide-react";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { StarRating } from "@/components/review/StarRating";
import { ReviewUserInfo } from "@/components/review/ReviewUserInfo";
import { ReviewVoteButtons } from "@/components/review/ReviewVoteButtons";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";
import { useAdminStatus } from "@/hooks/useAdminStatus";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { Review } from "@/types/review";

interface ReviewCardProps {
  review: Review;
}

export const ReviewCard = ({
  review
}: ReviewCardProps) => {
  const [upvoteCount, setUpvoteCount] = useState(review.upvotes || 0);
  const [downvoteCount, setDownvoteCount] = useState(review.downvotes || 0);
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const { user } = useAdminStatus();

  // Fetch real-time vote counts
  useEffect(() => {
    const fetchVoteCounts = async () => {
      try {
        // Get exact count of upvotes
        const { count: upCount, error: upError } = await supabase
          .from('review_votes')
          .select('*', { count: 'exact', head: true })
          .eq('review_id', review.id)
          .eq('vote_type', 'upvote');
          
        if (!upError) {
          setUpvoteCount(upCount || 0);
        }
        
        // Get exact count of downvotes
        const { count: downCount, error: downError } = await supabase
          .from('review_votes')
          .select('*', { count: 'exact', head: true })
          .eq('review_id', review.id)
          .eq('vote_type', 'downvote');
          
        if (!downError) {
          setDownvoteCount(downCount || 0);
        }
      } catch (error) {
        console.error('Error fetching vote counts:', error);
      }
    };

    fetchVoteCounts();
  }, [review.id]);

  // All users can view review details, but only logged-in users can interact with voting
  const reviewPath = `/review/${review.id}`;
  const authPath = "/auth";
  
  return (
    <Card className={`p-4 ${isMobile ? 'h-auto' : 'h-[280px]'} flex flex-col`}>
      <div className="flex flex-col flex-1">
        {/* Header Section */}
        <div className="mb-3">
          <div className="flex items-center justify-between gap-2">
            <Link to={reviewPath} className="flex items-center gap-2 min-w-0">
              <h3 className="text-lg font-semibold truncate dark:text-gray-100">{t(review.airport_name) || review.airport_name}</h3>
              <Plane className="h-4 w-4 text-film-400 dark:text-film-300 flex-shrink-0" />
            </Link>
            {user ? (
              <ReviewVoteButtons 
                reviewId={review.id} 
                initialUpvotes={upvoteCount} 
                initialDownvotes={downvoteCount} 
              />
            ) : (
              <Link to={authPath}>
                <ReviewVoteButtons 
                  reviewId={review.id} 
                  initialUpvotes={upvoteCount} 
                  initialDownvotes={downvoteCount}
                  disabled={true}
                />
              </Link>
            )}
          </div>
          <p className="text-sm text-film-500 dark:text-film-300 truncate">{t(review.airport_location) || review.airport_location}</p>
        </div>

        {/* User Info */}
        <Link to={reviewPath} className="flex-1 flex flex-col">
          {review.profiles && <ReviewUserInfo 
            userId={review.user_id} 
            username={review.profiles.username} 
            avatarUrl={review.profiles.avatar_url} 
            verificationStatus={review.profiles.verification_status} 
            isCreative={review.profiles.is_creative} 
          />}

          {/* Review Content */}
          <div className="flex-1 flex flex-col mt-2">
            <div className="space-y-2">
              {/* Film-Friendly Score */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-inherit">{t('film_friendly_score')}:</span>
                <StarRating score={review.film_friendly_score} />
              </div>

              {/* Hand Check */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-inherit">{t('hand_check')}:</span>
                <span className={`text-sm font-medium ${review.hand_check_success ? 'text-green-500 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
                  {review.hand_check_success ? t('successful') : t('unsuccessful')}
                </span>
              </div>

              {/* Review Summary */}
              <p className={`text-sm text-film-600 dark:text-gray-300 ${isMobile ? 'line-clamp-3' : 'line-clamp-2'}`}>
                {review.review_text}
              </p>
            </div>

            {/* Date - Always at the bottom */}
            <p className="text-xs text-film-400 dark:text-film-400/80 mt-auto pt-2">
              {format(new Date(review.created_at), 'MMM d, yyyy')}
            </p>
          </div>
        </Link>
      </div>
    </Card>
  );
};
